import { Link } from "@tanstack/react-router";
import {
  BookOpen,
  Copy,
  ExternalLink,
  FileText,
  GripVertical,
  Highlighter,
  MessageSquare,
  MoreHorizontal,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { formatRelative } from "../../lib/utils";
import { InlineNoteEditor } from "./InlineNoteEditor";

export type LivingDocItem = {
  id: string;
  /** "note" | "selection" | "doc-section" | "chat-answer" */
  item_type: string;
  title: string | null;
  selected_text: string | null;
  note_text: string | null;
  source_doc_id: string | null;
  source_section_id: string | null;
  /** Optional pre-resolved metadata (title of source doc + section label). */
  source_title?: string | null;
  source_version?: string | null;
  source_path?: string | null;
  added_by?: string;
  created_at: string;
  tags?: string[];
};

interface Props {
  item: LivingDocItem;
  onUpdateNote: (id: string, text: string) => void | Promise<void>;
  onRemove: (id: string) => void | Promise<void>;
  onCopy?: (item: LivingDocItem) => void;
  dragHandleProps?: React.HTMLAttributes<HTMLButtonElement>;
  isDragging?: boolean;
}

const KIND_META: Record<string, { label: string; Icon: typeof FileText; bg: string }> = {
  note: { label: "Apunte", Icon: BookOpen, bg: "bg-(--accent-50)" },
  selection: { label: "Cita", Icon: Highlighter, bg: "bg-(--bg-elevated)" },
  "doc-section": { label: "Sección", Icon: FileText, bg: "bg-(--bg-elevated)" },
  "chat-answer": { label: "Respuesta", Icon: MessageSquare, bg: "bg-(--bg-elevated)" },
};

/**
 * B2.1/B2.2 — Single block in a living-doc list.
 *
 * Renders source attribution (title + version + relative date), the content
 * itself (selection text in quote style, or InlineNoteEditor for note items),
 * and a small action toolbar (open source, copy, remove).
 */
export function LivingDocBlock({
  item,
  onUpdateNote,
  onRemove,
  onCopy,
  dragHandleProps,
  isDragging,
}: Props) {
  const meta = KIND_META[item.item_type] ?? KIND_META.selection;
  const Icon = meta?.Icon ?? FileText;

  return (
    <article
      data-testid="living-doc-block"
      data-block-id={item.id}
      data-kind={item.item_type}
      className={`relative flex gap-3 rounded-lg border border-(--border-subtle) ${
        meta?.bg ?? "bg-(--bg-elevated)"
      } p-4 transition-shadow ${isDragging ? "shadow-lg ring-1 ring-(--accent-400)" : ""}`}
    >
      <button
        type="button"
        data-testid="living-doc-drag-handle"
        aria-label="Reordenar"
        {...dragHandleProps}
        className="flex h-6 w-4 items-center justify-center text-(--text-tertiary) hover:text-(--text-secondary) cursor-grab active:cursor-grabbing"
      >
        <GripVertical size={14} />
      </button>

      <div className="flex-1 min-w-0">
        <header className="flex items-center gap-2 mb-1.5 text-[11px] text-(--text-tertiary)">
          <span className="inline-flex items-center gap-1 rounded-full bg-(--bg-canvas) border border-(--border-subtle) px-2 py-0.5 font-mono uppercase tracking-wider text-(--text-secondary)">
            <Icon size={10} />
            {meta?.label}
          </span>
          {item.source_title && (
            <span
              className="min-w-0 flex-1 truncate"
              data-testid="living-doc-source"
              title={item.source_title}
            >
              {item.source_title}
              {item.source_version && (
                <span className="text-(--text-tertiary)"> · {item.source_version}</span>
              )}
            </span>
          )}
          <span className="ml-auto whitespace-nowrap font-mono" data-testid="living-doc-when">
            {formatRelative(item.created_at)}
          </span>
        </header>

        {item.title && (
          <h3 className="mb-1.5 text-base font-semibold text-(--text-primary)">{item.title}</h3>
        )}

        {item.item_type === "note" ? (
          <InlineNoteEditor
            value={item.note_text ?? ""}
            onSave={(text) => onUpdateNote(item.id, text)}
            placeholder="Escribe tu apunte…"
            testId={`living-doc-editor-${item.id}`}
          />
        ) : item.item_type === "selection" ? (
          <ExpandableText text={item.selected_text ?? ""} testId="living-doc-selection" asQuote />
        ) : (
          <ExpandableText
            text={item.selected_text ?? item.note_text ?? ""}
            testId="living-doc-text"
          />
        )}

        {item.tags && item.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {item.tags.map((t) => (
              <span
                key={t}
                className="rounded-full bg-(--accent-50) border border-(--accent-100) px-2 py-0.5 font-mono text-[10px] text-(--accent-700)"
              >
                #{t}
              </span>
            ))}
          </div>
        )}
      </div>

      <div
        className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid="living-doc-actions"
        style={{ opacity: 1 }}
      >
        {item.source_doc_id && (
          <Link
            to="/docs/$docId"
            params={{ docId: item.source_doc_id }}
            aria-label="Abrir fuente"
            data-testid="living-doc-open-source"
            className="rounded p-1 text-(--text-tertiary) hover:text-(--text-primary) hover:bg-(--bg-hover) no-underline"
          >
            <ExternalLink size={13} />
          </Link>
        )}
        <button
          type="button"
          aria-label="Copiar como cita"
          data-testid="living-doc-copy"
          onClick={() => {
            const text = item.selected_text ?? item.note_text ?? "";
            void navigator.clipboard.writeText(text);
            onCopy?.(item);
            toast.success("Copiado");
          }}
          className="rounded p-1 text-(--text-tertiary) hover:text-(--text-primary) hover:bg-(--bg-hover)"
        >
          <Copy size={13} />
        </button>
        <button
          type="button"
          aria-label="Más opciones"
          className="rounded p-1 text-(--text-tertiary) hover:text-(--text-primary) hover:bg-(--bg-hover)"
        >
          <MoreHorizontal size={13} />
        </button>
        <button
          type="button"
          aria-label="Eliminar item"
          data-testid="living-doc-remove"
          onClick={() => {
            if (window.confirm("¿Eliminar este item del documento?")) {
              void onRemove(item.id);
            }
          }}
          className="rounded p-1 text-(--text-tertiary) hover:text-(--error) hover:bg-(--bg-hover)"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </article>
  );
}

/**
 * C1.5 — Texto largo en items: muestra completo si <= 600 chars, si no
 * preview + "Ver todo / Ver menos".
 */
function ExpandableText({
  text,
  testId,
  asQuote,
}: {
  text: string;
  testId?: string;
  asQuote?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const LIMIT = 600;
  const isLong = text.length > LIMIT;
  const visible = isLong && !expanded ? `${text.slice(0, LIMIT)}…` : text;
  const baseClass = asQuote
    ? "border-l-2 border-(--accent-400) pl-3 italic text-(--text-secondary)"
    : "whitespace-pre-wrap text-sm text-(--text-primary)";
  return (
    <>
      {asQuote ? (
        <blockquote className={baseClass} data-testid={testId}>
          "{visible}"
        </blockquote>
      ) : (
        <p className={baseClass} data-testid={testId} style={{ whiteSpace: "pre-wrap" }}>
          {visible}
        </p>
      )}
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="living-doc-text-toggle"
          className="mt-1 text-[10px] text-(--accent-600) hover:underline"
        >
          {expanded ? "Ver menos" : "Ver todo"}
        </button>
      )}
    </>
  );
}
